import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";

export const stages = { new: "Opportunity", qualifying: "Qualifying", pricing: "Pricing", rfq: "RFQ", reviewing: "Review", ready_to_submit: "Ready to submit", submitted: "Submitted", negotiation: "Negotiation", awarded: "Won", unsuccessful: "Lost", withdrawn: "Withdrawn", no_bid: "No bid", archived: "Archived" };
export const normalize = (s) => ({ draft: "new", pending: "pricing", review: "reviewing", won: "awarded", lost: "unsuccessful" }[s] || s || "new");
export const active = (t) => ["new", "qualifying", "pricing", "rfq", "reviewing", "ready_to_submit", "submitted", "negotiation"].includes(normalize(t.status));
export const awaiting = (t) => ["submitted", "negotiation"].includes(normalize(t.status));
export const money = (v) => v == null || v === "" ? "Not recorded" : new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 }).format(v);
export const localInput = (value) => {
  if (!value || Number.isNaN(new Date(value).getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-NZ", { timeZone: "Pacific/Auckland", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(value));
  const p = Object.fromEntries(parts.map(({ type, value: v }) => [type, v]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};
export const today = () => localInput(new Date().toISOString()).slice(0, 10);
export function dateText(v, time = false) {
  if (!v) return "Not recorded";
  const d = new Date(v.length === 10 ? `${v}T00:00:00+12:00` : v);
  if (Number.isNaN(d.getTime())) return "Check date";
  return new Intl.DateTimeFormat("en-NZ", { timeZone: "Pacific/Auckland", day: "numeric", month: "short", year: "numeric", ...(time && v.length > 10 ? { hour: "numeric", minute: "2-digit" } : {}) }).format(d);
}
export function due(t) {
  const v = t.closing_at || t.due_date;
  const day = t.closing_at ? localInput(t.closing_at).slice(0, 10) : (t.due_date || "").slice(0, 10);
  const days = day ? Math.round((Date.parse(day) - Date.parse(today())) / 86400000) : null;
  const overdue = active(t) && !awaiting(t) && days != null && (t.closing_at ? Date.parse(t.closing_at) < Date.now() : days < 0);
  let label = !v ? "No due date" : days === 0 ? "Due today" : days === 1 ? "1 day remaining" : days > 1 ? `${days} days remaining` : "Closing date passed";
  if (v && (days == null || Number.isNaN(days))) label = "Check due date";
  if (overdue) label = "Overdue";
  if (awaiting(t)) label = normalize(t.status) === "negotiation" ? "Awaiting outcome" : "Submitted";
  if (!active(t)) label = stages[normalize(t.status)] || normalize(t.status);
  return { days, overdue, label, value: v, soon: active(t) && !awaiting(t) && !overdue && days != null && days <= 7 && days >= 0 };
}
export function attention(t) {
  if (!active(t)) return [];
  const d = due(t), result = [];
  if (d.overdue) result.push("Overdue");
  else if (d.soon) result.push("Due soon");
  if (!t.owner_id && !t.estimator?.trim()) result.push("No owner");
  if (t.estimate == null) result.push("No estimated value");
  if (!t.client_name?.trim() || !t.description?.trim() || !d.value) result.push("Incomplete details");
  if (normalize(t.status) === "reviewing") result.push("Ready for review");
  const reviews = t.document_alerts?.review_required ?? t.document_review_count;
  if (reviews) result.push(`${reviews} document${reviews === 1 ? "" : "s"} to review`);
  return result;
}
export function StageBadge({ status }) {
  const s = normalize(status);
  return <Badge variant="outline" className={`whitespace-nowrap font-medium ${s === "awarded" ? "border-emerald-300 text-emerald-700 dark:text-emerald-300" : "text-foreground bg-muted/40"}`}>{stages[s] || s.replaceAll("_", " ")}</Badge>;
}
export function Choice({ label, value, onChange, options, className = "", disabled = false }) {
  return <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? "" : v)} disabled={disabled}><SelectTrigger aria-label={label} className={`h-10 ${className}`}><SelectValue placeholder={label} /></SelectTrigger><SelectContent>{options.map(([v, text]) => <SelectItem key={v || "__none"} value={v || "__none"}>{text}</SelectItem>)}</SelectContent></Select>;
}
export function Panel({ title, children, className = "" }) {
  return <Card className={`shadow-none ${className}`}><CardContent className="p-4 sm:p-5"><h2 className="text-sm font-semibold mb-4">{title}</h2>{children}</CardContent></Card>;
}
export function EmptyModule({ name }) {
  return <Panel title={name}><p className="text-sm text-muted-foreground">Coming in Tender workspace upgrade.</p></Panel>;
}
